import React, { useEffect, useState } from "react";
import { workScheduleApi, errorMessage } from "../adminApi";
import { useAdminToast } from "../components/Toast";
import { Card, Table, Badge, Button, Modal, Loading, ErrorState, PageHeader, Input, Select } from "../components/ui";

/** 0=Senin .. 6=Minggu (mengikuti dayofweek Odoo). */
const DAYS = [
  { k: "0", label: "Senin" },
  { k: "1", label: "Selasa" },
  { k: "2", label: "Rabu" },
  { k: "3", label: "Kamis" },
  { k: "4", label: "Jumat" },
  { k: "5", label: "Sabtu" },
  { k: "6", label: "Minggu" },
];

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/** Ringkas objek days → "Sen–Jum 08:00–17:00" atau daftar bila tak seragam. */
function summarizeDays(days) {
  const entries = DAYS.filter((d) => days && days[d.k]);
  if (entries.length === 0) return "—";
  const jam = (d) => `${days[d.k].start}–${days[d.k].end}`;
  const allSame = entries.every((d) => jam(d) === jam(entries[0]));
  if (allSame) {
    return `${entries[0].label.slice(0, 3)}–${entries[entries.length - 1].label.slice(0, 3)} ${jam(entries[0])}`;
  }
  return entries.map((d) => `${d.label.slice(0, 3)} ${jam(d)}`).join(", ");
}

export default function WorkSchedulePage() {
  const toast = useAdminToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recalc, setRecalc] = useState({ date_from: firstOfMonth(), date_to: today(), running: false });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await workScheduleApi.list({ limit: 200 });
      setRows(res.rows || []);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (payload) => {
    setBusy(true);
    try {
      await workScheduleApi.update(editing.id, payload);
      toast.success("Jadwal diperbarui (dikunci dari sync Odoo)");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const runRecompute = async () => {
    setRecalc((r) => ({ ...r, running: true }));
    try {
      const res = await workScheduleApi.recompute({ date_from: recalc.date_from, date_to: recalc.date_to });
      toast.success(`Recompute selesai: ${res.updated}/${res.total} record diperbarui`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setRecalc((r) => ({ ...r, running: false }));
    }
  };

  const columns = [
    {
      key: "name",
      header: "Jadwal",
      render: (r) => (
        <div>
          <div className="font-medium text-slate-800">{r.name}</div>
          <div className="text-[11px] text-slate-400">
            {r.odoo_calendar_id ? `Odoo cal #${r.odoo_calendar_id}` : "lokal"}
          </div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Tipe",
      render: (r) =>
        r.flexible ? <Badge color="purple">Flexible</Badge> : <Badge color="blue">Fix</Badge>,
    },
    {
      key: "jam",
      header: "Jam Kerja",
      render: (r) =>
        r.flexible ? (
          <span className="text-slate-400 text-xs italic">fallback jam work location</span>
        ) : (
          <span className="text-xs">{summarizeDays(r.days)}</span>
        ),
    },
    {
      key: "tol",
      header: "Toleransi",
      render: (r) => <span className="text-xs">{r.late_tolerance_min != null ? `${r.late_tolerance_min} mnt` : "default"}</span>,
    },
    { key: "emp", header: "Karyawan", render: (r) => <Badge color="gray">{r.employee_count ?? 0}</Badge> },
    { key: "lock", header: "Sumber", render: (r) => (r.locked ? <Badge color="yellow">Override manual</Badge> : <Badge color="green">Sinkron Odoo</Badge>) },
    {
      key: "act",
      header: "",
      render: (r) => (
        <div className="flex justify-end">
          <Button size="sm" variant="secondary" onClick={() => setEditing(r)}>Edit</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Jadwal Kerja"
        subtitle="Dipetakan otomatis dari working schedule Odoo. Telat/half-day di dashboard dihitung dari jam ini; jadwal flexible memakai jam work location. Edit = override manual (tak ditimpa sync)."
        action={<Button variant="secondary" size="sm" onClick={load}>↻ Refresh</Button>}
      />

      {/* Panel recompute */}
      <Card className="p-3 mb-4 shrink-0">
        <div className="flex flex-wrap items-end gap-2">
          <div className="text-sm text-slate-600 mr-2">
            <div className="font-medium text-slate-700">Hitung ulang aturan absensi</div>
            <div className="text-[11px] text-slate-400">Terapkan jadwal terbaru ke record lama (jam ekspektasi + telat/half-day record app).</div>
          </div>
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Dari</span>
            <input type="date" value={recalc.date_from} onChange={(e) => setRecalc((r) => ({ ...r, date_from: e.target.value }))} className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Sampai</span>
            <input type="date" value={recalc.date_to} onChange={(e) => setRecalc((r) => ({ ...r, date_to: e.target.value }))} className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          <Button onClick={runRecompute} disabled={recalc.running || !recalc.date_from || !recalc.date_to}>
            {recalc.running ? "Menghitung…" : "Recompute"}
          </Button>
        </div>
      </Card>

      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : (
          <Card className="flex-1 min-h-0 overflow-hidden">
            <Table columns={columns} rows={rows} empty="Belum ada jadwal kerja. Jalankan sync master data Odoo dulu." />
          </Card>
        )}
      </div>

      <ScheduleFormModal editing={editing} busy={busy} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function ScheduleFormModal({ editing, busy, onClose, onSave }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!editing) return setForm(null);
    // Prefill baris hari; hari tak terjadwal → tidak aktif.
    const days = {};
    for (const d of DAYS) {
      const line = editing.days?.[d.k];
      days[d.k] = { active: !!line, start: line?.start || "08:00", end: line?.end || "17:00" };
    }
    setForm({
      name: editing.name || "",
      flexible: !!editing.flexible,
      late_tolerance_min: editing.late_tolerance_min ?? "",
      halfday_max_minutes: editing.halfday_max_minutes ?? "",
      days,
    });
  }, [editing]);

  if (!editing || !form) return null;

  const setDay = (k, patch) =>
    setForm((f) => ({ ...f, days: { ...f.days, [k]: { ...f.days[k], ...patch } } }));

  const submit = () => {
    const days = {};
    if (!form.flexible) {
      for (const d of DAYS) {
        const row = form.days[d.k];
        if (row.active) days[d.k] = { start: row.start, end: row.end };
      }
    }
    onSave({
      name: form.name,
      flexible: form.flexible,
      late_tolerance_min: form.late_tolerance_min === "" ? null : Number(form.late_tolerance_min),
      halfday_max_minutes: form.halfday_max_minutes === "" ? null : Number(form.halfday_max_minutes),
      days,
    });
  };

  return (
    <Modal
      open={!!editing}
      onClose={onClose}
      title={`Edit Jadwal • ${editing.name}`}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy || !form.name}>{busy ? "Menyimpan…" : "Simpan (kunci)"}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Nama Jadwal" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Select
            label="Tipe"
            value={form.flexible ? "flex" : "fix"}
            onChange={(e) => setForm((f) => ({ ...f, flexible: e.target.value === "flex" }))}
          >
            <option value="fix">Fix (jam tetap per hari)</option>
            <option value="flex">Flexible (fallback jam work location)</option>
          </Select>
          <Input
            label="Toleransi Telat (menit) — kosong = default"
            type="number"
            value={form.late_tolerance_min}
            onChange={(e) => setForm((f) => ({ ...f, late_tolerance_min: e.target.value }))}
            placeholder="mis. 5"
          />
          <Input
            label="Batas Half-day (menit kerja) — kosong = default 420 (7 jam)"
            type="number"
            value={form.halfday_max_minutes}
            onChange={(e) => setForm((f) => ({ ...f, halfday_max_minutes: e.target.value }))}
            placeholder="mis. 420"
          />
        </div>

        {form.flexible ? (
          <div className="text-sm text-slate-500 bg-amber-50 border border-amber-100 rounded-lg p-3">
            Jadwal <b>flexible</b>: telat dihitung dari jam <b>check-in/out work location</b> karyawan
            (seperti Odoo). Tidak perlu isi jam per hari.
          </div>
        ) : (
          <div>
            <div className="text-sm font-medium text-slate-600 mb-2">Jam per hari (hari tak dicentang = libur, tak dihitung telat)</div>
            <div className="space-y-1.5">
              {DAYS.map((d) => {
                const row = form.days[d.k];
                return (
                  <div key={d.k} className="flex items-center gap-3">
                    <label className="flex items-center gap-2 w-28 shrink-0 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.active}
                        onChange={(e) => setDay(d.k, { active: e.target.checked })}
                        className="rounded border-slate-300 text-brand-500 focus:ring-brand-400"
                      />
                      {d.label}
                    </label>
                    <input
                      type="time"
                      value={row.start}
                      disabled={!row.active}
                      onChange={(e) => setDay(d.k, { start: e.target.value })}
                      className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                    <span className="text-slate-400">–</span>
                    <input
                      type="time"
                      value={row.end}
                      disabled={!row.active}
                      onChange={(e) => setDay(d.k, { end: e.target.value })}
                      className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-[11px] text-slate-400">
          Menyimpan akan <b>mengunci</b> jadwal ini agar tidak ditimpa sync Odoo berikutnya.
          Jalankan <b>Recompute</b> setelahnya untuk memperbarui record absensi lama.
        </div>
      </div>
    </Modal>
  );
}
